/**
 * Typed REST client for the gmd-review backend.
 *
 * Every endpoint returns a Promise that rejects with an `ApiError` (carrying
 * the HTTP status + parsed body) when the response is non-2xx, so screens can
 * branch on `err.status` without re-parsing.
 *
 * In the M1 dev setup Vite proxies /api → http://localhost:8002. In production
 * the SPA is served by the FastAPI container itself, so same-origin /api is
 * already correct.
 */

export interface PreprintRow {
  egusphere_id: string;
  doi: string;
  title: string;
  lead_author: string;
  coauthors: string[];
  posting_date: string; // ISO date
  landing_url: string;
  deposit_count: number | null;
  // Reproducibility-scan surface (optional: absent on older API responses / test stubs).
  repro_status?: string | null; // pending | done | skipped | failed | null
  repro_error?: string | null; // why the scan failed (issue #112) — tooltip text
  summary_source?: string | null; // "generated" | "manual" | null
  repro_passed?: number | null; // generated scans only
  repro_total?: number | null; // generated scans only
  // Whether the Preprint tab can render an inline PDF (#190): true for a
  // Copernicus/arXiv manuscript, false for a host we can't build a direct-PDF
  // URL for (EarthArXiv/other) — the tab links out instead.
  paper_pdf_available?: boolean;
}

export interface DepositRow {
  kind: string;
  identifier: string;
  url: string;
  role: string;
  size_bytes: number | null;
  source: string;
}

export interface PreprintDetail {
  preprint: PreprintRow;
  deposits: DepositRow[];
}

export interface SessionResponse {
  id: number;
  egusphere_id: string;
  status: "pending" | "ingesting" | "ready" | "failed";
  file_count: number;
  total_bytes: number;
  error: string | null;
  created_at: string;
  updated_at: string;
}

export interface TreeNode {
  id: string;
  name: string;
  isDir: boolean;
  size: number | null;
  children?: TreeNode[];
  /** Set on M4.3 lazy stubs — files catalogued at ingest time but not
   *  yet on disk. Click triggers the download endpoint. */
  lazyFileId?: number;
  lazyStatus?: "pending" | "downloading" | "ready" | "failed";
}

export type FileResponse =
  | {
      mode: "text";
      language: string | null;
      content: string;
      size_bytes: number;
      truncated: boolean;
    }
  | {
      mode: "csv";
      columns: string[];
      rows: unknown[][];
      total_rows: number;
      offset: number;
      limit: number;
    }
  | {
      mode: "netcdf";
      dimensions: Record<string, number>;
      variables: {
        name: string;
        dtype: string;
        shape: number[];
        dims: string[];
        attrs: Record<string, unknown>;
      }[];
      attrs: Record<string, unknown>;
      file_size_bytes: number;
    }
  | {
      mode: "binary";
      mime: string | null;
      size_bytes: number;
    };

export interface SummaryResponse {
  egusphere_id: string;
  slug: string;
  markdown: string;
}

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown) {
    super(`API ${status}`);
    this.status = status;
    this.body = body;
  }
}

async function request<T>(input: string, init?: RequestInit): Promise<T> {
  const res = await fetch(input, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers ?? {}),
    },
  });
  const text = await res.text();
  const body = text ? JSON.parse(text) : null;
  if (!res.ok) {
    throw new ApiError(res.status, body);
  }
  return body as T;
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

export function searchPreprints(q: string, since?: string): Promise<PreprintRow[]> {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  if (since) params.set("since", since);
  return request(`/api/preprints/search?${params.toString()}`);
}

export function getPreprint(egusphereId: string): Promise<PreprintDetail> {
  return request(`/api/preprints/${egusphereId}`);
}

export function createSession(egusphereId: string): Promise<SessionResponse> {
  return request(`/api/sessions`, {
    method: "POST",
    body: JSON.stringify({ egusphere_id: egusphereId }),
  });
}

export function getSession(sid: number): Promise<SessionResponse> {
  return request(`/api/sessions/${sid}`);
}

export function getTree(sid: number): Promise<TreeNode> {
  return request(`/api/sessions/${sid}/tree`);
}

export function getFile(
  sid: number,
  path: string,
  opts: { offset?: number; limit?: number } = {},
): Promise<FileResponse> {
  const params = new URLSearchParams({ path });
  if (opts.offset !== undefined) params.set("offset", String(opts.offset));
  if (opts.limit !== undefined) params.set("limit", String(opts.limit));
  return request(`/api/sessions/${sid}/file?${params.toString()}`);
}

export function rawFileUrl(sid: number, path: string): string {
  return `/api/sessions/${sid}/raw?path=${encodeURIComponent(path)}`;
}

/** Same-origin streaming URL for a preprint's manuscript PDF (#190). */
export function preprintPdfUrl(egusphereId: string): string {
  return `/api/preprints/${encodeURIComponent(egusphereId)}/pdf`;
}

export function getSummary(egusphereId: string): Promise<SummaryResponse> {
  return request(`/api/summary/${egusphereId}`);
}

// ---------------------------------------------------------------------------
// Lazy files (M4.3)
// ---------------------------------------------------------------------------

export type LazyFileStatus = "pending" | "downloading" | "ready" | "failed";

export interface LazyFileRow {
  id: number;
  session_id: number;
  rel_path: string;
  size_bytes: number;
  status: LazyFileStatus;
  progress_bytes: number;
  is_archive: boolean;
  error: string | null;
  downloaded_at: string | null;
}

export function listLazyFiles(sid: number): Promise<LazyFileRow[]> {
  return request(`/api/sessions/${sid}/lazy_files`);
}

export function getLazyFile(sid: number, lfid: number): Promise<LazyFileRow> {
  return request(`/api/sessions/${sid}/lazy_files/${lfid}`);
}

export function triggerLazyDownload(
  sid: number,
  lfid: number,
): Promise<LazyFileRow> {
  return request(`/api/sessions/${sid}/lazy_files/${lfid}/download`, {
    method: "POST",
  });
}

// ---------------------------------------------------------------------------
// Auth (M4.1)
// ---------------------------------------------------------------------------

export interface CurrentUser {
  id: number;
  email: string;
  name: string;
  orcid_id: string | null;
}

/** Returns the signed-in user, or rejects with ApiError(401|403). The SPA
 * branches on the status: 401 → redirect to Cognito hosted UI; 403 →
 * render the "not yet authorized" page (the body's detail field carries
 * the ORCID iD for display). */
export function getCurrentUser(): Promise<CurrentUser> {
  return request("/api/auth/me");
}

/** Navigate to GET /api/auth/signout. The backend clears the HttpOnly
 * cookie and 302s to Cognito's /logout (which tears down the user-pool
 * session, then redirects back to the app). This MUST be a top-level
 * navigation — a background fetch can't carry the browser through the
 * cross-origin Cognito redirect, so the session would survive. */
export function signOut(): void {
  window.location.href = "/api/auth/signout";
}

// ---------------------------------------------------------------------------
// Executions (M3)
// ---------------------------------------------------------------------------

export interface ProfileInfo {
  name: string;
  instance_type: string;
  vcpus: number;
  memory_gib: number;
  gpu: string | null;
  ebs_gib: number;
  cents_per_hour: number;
}

export type ExecutionStatus =
  | "pending_approval"
  | "approved"
  | "dispatching"
  | "running"
  | "completed"
  | "failed"
  | "cancelled";

export interface BudgetView {
  estimated_cents: number;
  session_committed_cents: number;
  user_committed_cents: number;
  per_execution_cap_cents: number;
  per_session_cap_cents: number;
  per_user_month_cap_cents: number;
  ok: boolean;
  reason: string | null;
}

export interface ExecutionRow {
  id: number;
  session_id: number;
  status: ExecutionStatus;
  profile: string;
  command: string;
  expect_output_globs: string[];
  estimated_wall_minutes: number;
  estimated_cost_cents: number;
  actual_wall_seconds: number | null;
  actual_cost_cents: number | null;
  instance_id: string | null;
  ssm_command_id: string | null;
  exit_code: number | null;
  stdout_tail: string | null;
  error: string | null;
  budget: BudgetView;
}

export function getExecution(sid: number, eid: number): Promise<ExecutionRow> {
  return request(`/api/sessions/${sid}/executions/${eid}`);
}

export function listExecutions(sid: number): Promise<ExecutionRow[]> {
  return request(`/api/sessions/${sid}/executions`);
}

export function approveExecution(sid: number, eid: number): Promise<ExecutionRow> {
  return request(`/api/sessions/${sid}/executions/${eid}/approve`, { method: "POST" });
}

export function cancelExecution(sid: number, eid: number): Promise<ExecutionRow> {
  return request(`/api/sessions/${sid}/executions/${eid}/cancel`, { method: "POST" });
}

export function retryExecution(sid: number, eid: number): Promise<ExecutionRow> {
  return request(`/api/sessions/${sid}/executions/${eid}/retry`, { method: "POST" });
}

export type ExecutionEvent =
  | { event: "status"; data: { status?: ExecutionStatus; message?: string; instance_id?: string; ssm_command_id?: string; exit_code?: number | null; stdout_tail?: string | null; error?: string | null; written?: string[] } }
  | { event: "stdout"; data: { chunk: string } }
  | { event: "done"; data: { status: ExecutionStatus; exit_code: number | null } }
  | { event: "error"; data: { message: string } };

export async function* streamExecution(
  sid: number,
  eid: number,
  signal?: AbortSignal,
): AsyncGenerator<ExecutionEvent> {
  const res = await fetch(`/api/sessions/${sid}/executions/${eid}/stream`, {
    method: "GET",
    headers: { Accept: "text/event-stream" },
    signal,
  });
  if (!res.ok) {
    throw new ApiError(res.status, await res.text());
  }
  if (!res.body) throw new Error("No SSE body");
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let split: number;
    while ((split = buffer.indexOf("\n\n")) !== -1) {
      const frame = buffer.slice(0, split);
      buffer = buffer.slice(split + 2);
      let event = "";
      let dataLine = "";
      for (const line of frame.split("\n")) {
        if (line.startsWith("event:")) event = line.slice(6).trim();
        else if (line.startsWith("data:")) dataLine += line.slice(5).trim();
      }
      if (event && dataLine) {
        try {
          yield { event, data: JSON.parse(dataLine) } as ExecutionEvent;
        } catch {
          // ignore malformed frames
        }
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Chat
// ---------------------------------------------------------------------------

/** One block in an assistant or user (tool result) message — Anthropic content shape. */
export type AnthropicBlock =
  | { type: "text"; text: string }
  | { type: "tool_use"; id: string; name: string; input: Record<string, unknown> }
  | { type: "tool_result"; tool_use_id: string; content: string; is_error?: boolean };

export interface ChatHistoryRow {
  role: "user" | "assistant";
  content: AnthropicBlock[];
  usage: Record<string, number> | null;
  sequence: number;
}

export function getChatHistory(sid: number): Promise<ChatHistoryRow[]> {
  return request(`/api/sessions/${sid}/chat`);
}

export function clearChat(sid: number): Promise<{ deleted: number }> {
  return request(`/api/sessions/${sid}/chat`, { method: "DELETE" });
}

/**
 * SSE event yielded by the agent loop.
 *
 * `delta`         streamed token of Claude's current text
 * `tool_use_start` Claude has emitted a tool_use block (name known, input may still be streaming)
 * `tool_use_end`   the backend has executed the tool; result preview attached
 * `done`           the final assistant turn finished
 * `error`          surfaced from Anthropic or our agent loop
 */
export type ChatEvent =
  | { event: "delta"; data: { type: "text_delta"; text: string } }
  | { event: "tool"; data: { type: "tool_use_start"; id: string; name: string } }
  | {
      event: "tool";
      data: {
        type: "tool_use_end";
        id: string;
        name: string;
        input: Record<string, unknown>;
        result_preview: string;
        artifact_path: string | null;
        is_error: boolean;
      };
    }
  | { event: "done"; data: { stop_reason: string; usage: Record<string, number>; iterations: number } }
  | { event: "error"; data: { message: string; status?: number } };

/**
 * POST /api/sessions/{sid}/chat — stream Server-Sent Events from the agent loop.
 *
 * Yields ChatEvent objects as they arrive. The caller is responsible for
 * updating UI state; we just parse the SSE stream.
 */
export async function* streamChat(
  sid: number,
  message: string,
  signal?: AbortSignal,
): AsyncGenerator<ChatEvent> {
  const res = await fetch(`/api/sessions/${sid}/chat`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
    body: JSON.stringify({ message }),
    signal,
  });
  if (!res.ok) {
    const body = await res.text();
    throw new ApiError(res.status, body);
  }
  if (!res.body) throw new Error("Stream has no body");

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    // SSE frames are separated by a blank line ("\n\n").
    let split: number;
    while ((split = buffer.indexOf("\n\n")) !== -1) {
      const frame = buffer.slice(0, split);
      buffer = buffer.slice(split + 2);
      const ev = parseFrame(frame);
      if (ev) yield ev;
    }
  }
}

function parseFrame(frame: string): ChatEvent | null {
  let event = "";
  let dataLine = "";
  for (const line of frame.split("\n")) {
    if (line.startsWith("event:")) event = line.slice(6).trim();
    else if (line.startsWith("data:")) dataLine += line.slice(5).trim();
  }
  if (!event || !dataLine) return null;
  try {
    return { event, data: JSON.parse(dataLine) } as ChatEvent;
  } catch {
    return null;
  }
}
